import {UserPreferences} from "./types";
import {
    fetchRemotePreferences,
    loadLocalPreferences,
    LOCAL_KEYS,
    saveLocalPreference,
    updateRemotePreference
} from "./preferencesPersistence";
import {
    BarButtonsDefault,
    defaultAppShortcuts,
    defaultHome,
    squadflixHomeDefaultPreferences
} from "./preferencesDefaults";

class PreferencesStore {
    private prefs: UserPreferences = {barButtons: [], home: defaultHome};
    private eventTarget = new EventTarget();
    private loading = false;
    private fetchId = 0;
    private loadedUserId: string | null = null;

    get state() {
        return this.prefs;
    }

    get isLoading() {
        return this.loading;
    }

    get currentUserId() {
        return this.loadedUserId;
    }

    subscribe(cb: (prefs: UserPreferences) => void) {
        const handler = (evt: any) => cb(evt.detail);
        this.eventTarget.addEventListener("update", handler);
        cb(this.prefs); // estado inicial
        return () => this.eventTarget.removeEventListener("update", handler);
    }

    async fetch(userId: string) {
        if (this.loading) return;

        this.loading = true;
        this.fetchId++;
        const currentId = this.fetchId;

        const [remote, local] = await Promise.all([
            fetchRemotePreferences(),
            Promise.resolve(loadLocalPreferences())
        ]);

        if (currentId !== this.fetchId) return; // Llegó tarde → ignorar

        this.prefs = this.mergePreferences(remote, local);
        this.loading = false;
        this.loadedUserId = userId;
        this.notify();
    }

    clear() {
        this.prefs = {barButtons: [], home: defaultHome};
        this.loadedUserId = null;
        this.notify();
    }

    async updatePreference(key: string, value: any) {
        // ¿Es local?
        if (LOCAL_KEYS.includes(key)) {
            saveLocalPreference(key as any, value);
            this.prefs = {...this.prefs, [key]: value};
            this.notify();
            return;
        }

        // Preferencia remota
        await updateRemotePreference(key, value);

        // Actualizar instantáneamente (optimistic update)
        this.prefs = {...this.prefs, [key]: value};
        this.notify();
    }

    private notify() {
        this.eventTarget.dispatchEvent(new CustomEvent("update", {detail: this.prefs}));
    }

    private mergePreferences(remote: any, local: any): UserPreferences {
        return {
            barButtons: BarButtonsDefault,
            home: defaultHome,
            shortcuts: defaultAppShortcuts,
            ...remote,
            ...local,
            squadflixHomePreferences: {
                ...squadflixHomeDefaultPreferences,
                ...(remote.squadflixHomePreferences ?? {})
            }
        };
    }
}

export const preferencesStore = new PreferencesStore();
